import React from 'react';
import {
  FolderTree,
  Building2,
  MapPin,
  Warehouse,
  Package,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Settings,
  Globe,
  Clock,
  Calendar,
  Banknote,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import {
  OrgChartNode as OrgChartNodeType,
  NodeType,
  NODE_COLORS,
  NODE_TYPE_LABELS,
  ALLOWED_CHILDREN,
  LAYOUT_CONSTANTS,
} from './types';

interface OrgChartNodeProps {
  node: OrgChartNodeType;
  isSelected: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  onSelect: (node: OrgChartNodeType) => void;
  onToggleExpand: (nodeId: string) => void;
  onAddChild: (parentNode: OrgChartNodeType, childType: NodeType) => void;
  onEdit: (node: OrgChartNodeType) => void;
  onDelete: (node: OrgChartNodeType) => void;
  style?: React.CSSProperties;
}

const NODE_ICONS: Record<NodeType, React.ElementType> = {
  group: FolderTree,
  division: Building2,
  company: MapPin,
  warehouse: Warehouse,
  location: Package,
};

export function OrgChartNodeComponent({
  node,
  isSelected,
  isExpanded,
  hasChildren,
  onSelect,
  onToggleExpand,
  onAddChild,
  onEdit,
  onDelete,
  style,
}: OrgChartNodeProps) {
  const colors = NODE_COLORS[node.type];
  const Icon = NODE_ICONS[node.type];
  const label = NODE_TYPE_LABELS[node.type];
  const allowedChildren = ALLOWED_CHILDREN[node.type];

  // Get location display
  const locationText = node.metadata?.city
    ? `${node.metadata.city}${node.metadata.country ? `, ${node.metadata.country}` : ''}`
    : node.metadata?.headquarters_country || '';

  // Get effective settings display
  const currencyDisplay = node.effective_settings?.currency?.value ||
    node.regional_settings?.currency || '';
  const timezoneDisplay = node.effective_settings?.timezone?.value ||
    node.regional_settings?.timezone || '';
  const dateFormatDisplay = node.effective_settings?.date_format?.value ||
    node.regional_settings?.date_format || '';

  // Check if settings are inherited vs overridden (has local override)
  const hasOverriddenSettings = node.regional_settings?.currency ||
    node.regional_settings?.timezone ||
    node.regional_settings?.date_format;

  // Format timezone for display (e.g., "Europe/London" -> "London")
  const shortTimezone = timezoneDisplay ? timezoneDisplay.split('/').pop() : '';

  return (
    <div
      className={cn(
        'absolute cursor-pointer transition-all duration-200',
        'rounded-lg border-2 shadow-sm hover:shadow-md',
        colors.bg,
        colors.border,
        isSelected && 'ring-2 ring-primary ring-offset-2'
      )}
      style={{
        ...style,
        width: LAYOUT_CONSTANTS.NODE_WIDTH,
        minHeight: LAYOUT_CONSTANTS.NODE_HEIGHT,
      }}
      onClick={() => onSelect(node)}
    >
      {/* Header with type badge and expand/collapse */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-inherit">
        <Badge className={cn('text-xs text-white', colors.badge)}>
          {label}
        </Badge>
        <div className="flex items-center gap-1">
          {hasOverriddenSettings && (
            <span title="Custom settings">
              <Settings className="h-3 w-3 text-amber-500" />
            </span>
          )}
          {hasChildren && (
            <button
              className="p-0.5 hover:bg-black/10 rounded"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(node.id);
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Node content */}
      <div className="px-3 py-2 space-y-1">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-4 w-4 flex-shrink-0', colors.text)} />
          <span className="font-medium text-sm truncate" title={node.name}>
            {node.name}
          </span>
        </div>

        {node.code && (
          <div className="text-xs text-muted-foreground">
            Code: {node.code}
          </div>
        )}

        {locationText && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Globe className="h-3 w-3" />
            <span className="truncate">{locationText}</span>
          </div>
        )}

        {/* Regional settings row */}
        {(currencyDisplay || shortTimezone || dateFormatDisplay) && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
            {currencyDisplay && (
              <span className="flex items-center gap-0.5" title={`Currency: ${currencyDisplay}`}>
                <Banknote className="h-3 w-3" />
                {currencyDisplay}
              </span>
            )}
            {shortTimezone && (
              <span className="flex items-center gap-0.5" title={`Timezone: ${timezoneDisplay}`}>
                <Clock className="h-3 w-3" />
                {shortTimezone}
              </span>
            )}
            {dateFormatDisplay && (
              <span className="flex items-center gap-0.5" title={`Date Format: ${dateFormatDisplay}`}>
                <Calendar className="h-3 w-3" />
                {dateFormatDisplay}
              </span>
            )}
          </div>
        )}

        {/* Status badge */}
        {!node.is_active && (
          <Badge variant="secondary" className="text-xs">
            Inactive
          </Badge>
        )}

        {/* Portal mode indicator for companies */}
        {node.type === 'company' && node.portal_mode && (
          <Badge variant="outline" className="text-xs">
            {node.portal_mode === 'both' ? 'Both Portals' :
             node.portal_mode === 'customer' ? 'Customer' : 'Supplier'}
          </Badge>
        )}
      </div>

      {/* Action buttons (always visible) */}
      <div className="flex items-center justify-center gap-1 border-t border-inherit px-2 py-1.5 bg-background/50">
        {allowedChildren.length > 0 && (
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              onAddChild(node, allowedChildren[0]);
            }}
            title={`Add ${NODE_TYPE_LABELS[allowedChildren[0]]}`}
          >
            <Plus className="h-3 w-3" />
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(node);
          }}
          title="Edit"
        >
          <Pencil className="h-3 w-3" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 text-destructive hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(node);
          }}
          title="Delete"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export default OrgChartNodeComponent;
